/**
 * Public Interview API service.
 *
 * Handles all API calls for the public-facing interview experience.
 * These endpoints do NOT require authentication — the interview token
 * acts as the access mechanism.
 */

const BASE_URL = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000').replace(/\/$/, '');

// ========================================
// INTERFACES
// ========================================

export interface TopicInfo {
  index: number;
  id: string;
  name: string;
}

export interface PublicInterviewerInfo {
  name: string | null;
  description: string | null;
  logoUrl: string | null;
  total_topics: number;
  topics: TopicInfo[];
}

export interface PublicStartResponse {
  session_id: string;
  first_question: string;
  suggested_answers: string[];
  suggestion_selection_type?: 'single' | 'multi';
  total_topics: number;
  current_topic_index: number;
  topics: TopicInfo[];
  topic_confidence: Record<string, number>;
  sidebar_knowledge: SidebarKnowledge | null;
}

type PublicInterviewMode = 'default' | 'open_questions';

export interface PublicNavigateTopicResponse {
  session_id: string;
  question: string;
  suggested_answers: string[];
  suggestion_selection_type?: 'single' | 'multi';
  current_topic_index: number;
  total_topics: number;
  topic_name: string;
}

export interface PublicCompleteResponse {
  session_id: string;
  summary: string;
  overall_confidence: number;
}

export interface PublicSummaryResponse {
  session_id: string;
  summary: string | null;
  is_complete: boolean;
}

export interface PublicEntryData {
  id: string;
  speaker: 'interviewer' | 'user';
  content: string;
  topic_id: string | null;
  question_type: string | null;
  extracted_insights: Record<string, any> | null;
  sequence_number: number;
  created_at: string;
}

export interface PublicStartOrResumeResponse {
  session_id: string;
  is_resumed: boolean;
  current_topic_index: number;
  total_topics: number;
  topics: TopicInfo[];
  topic_confidence: Record<string, number>;
  entries: PublicEntryData[];
  first_question: string | null;
  suggested_answers: string[];
  suggestion_selection_type?: 'single' | 'multi';
  is_reviewing: boolean;
  review_summaries: TopicReviewSummary[] | null;
  sidebar_knowledge: SidebarKnowledge | null;
}

export interface TopicReviewSummary {
  topic_id: string;
  topic_name: string;
  summary: string;
  edited_summary: string | null;
  confirmed: boolean;
  confidence_level: number;
  generated_at: string | null;
}

// ========================================
// KNOWLEDGE SIDEBAR INTERFACES
// ========================================

export interface CompanySummary {
  name: string | null;
  one_liner: string | null;
  category: string | null;
}

export interface KnowledgeInsight {
  field: string;
  value: any;
  source_entry_id: string | null;
}

export interface TopicKnowledge {
  topic_name: string;
  synthesis_sections: Record<string, any>;
  live_insights: KnowledgeInsight[];
}

export interface SidebarKnowledge {
  company_summary: CompanySummary;
  topics: Record<string, TopicKnowledge>;
}

export interface KnowledgeDelta {
  topic_id: string;
  new_insights: KnowledgeInsight[];
  updated_fields: string[];
}

export interface SaveCorrectionResponse {
  session_id: string;
  corrections: Record<string, any>;
}

export interface GenerateReviewResponse {
  session_id: string;
  topic_summaries: TopicReviewSummary[];
  overall_confidence: number;
  topic_confidence: Record<string, number>;
}

export interface UpdateReviewResponse {
  session_id: string;
  topic_id: string;
  topic_confidence: Record<string, number>;
}

export interface ConfirmReviewResponse {
  session_id: string;
  summary: string;
  overall_confidence: number;
}

export interface PublicStreamEvent {
  event_type:
    | 'content'
    | 'suggested_answers'
    | 'progress'
    | 'confidence_update'
    | 'topic_change'
    | 'complete'
    | 'error'
    | 'done';
  data: Record<string, any>;
}

// ========================================
// HELPER
// ========================================

async function publicFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    let detail: string | undefined;
    try { detail = JSON.parse(text)?.detail; } catch { /* non-JSON body */ }
    throw new Error(
      detail ||
      `Request failed: ${response.status} ${response.statusText}${text ? ` — ${text.slice(0, 300)}` : ''}`
    );
  }

  return response.json();
}

// ========================================
// SERVICE
// ========================================

class PublicInterviewService {
  private prefix = '/api/public/interview';

  /**
   * Resolve a token to get interviewer info.
   */
  async resolveToken(token: string): Promise<PublicInterviewerInfo> {
    return publicFetch<PublicInterviewerInfo>(`${this.prefix}/${token}`);
  }

  /**
   * Start a new interview session.
   */
  async startInterview(
    token: string,
    intervieweeName?: string,
    intervieweeEmail?: string,
    mode: PublicInterviewMode = 'default'
  ): Promise<PublicStartResponse> {
    return publicFetch<PublicStartResponse>(`${this.prefix}/${token}/start`, {
      method: 'POST',
      body: JSON.stringify({
        interviewee_name: intervieweeName || null,
        interviewee_email: intervieweeEmail || null,
        mode,
      }),
    });
  }

  /**
   * Start or resume an interview session by email.
   */
  async startOrResume(
    token: string,
    email: string,
    name?: string,
    mode: PublicInterviewMode = 'default'
  ): Promise<PublicStartOrResumeResponse> {
    return publicFetch<PublicStartOrResumeResponse>(`${this.prefix}/${token}/start-or-resume`, {
      method: 'POST',
      body: JSON.stringify({
        email: email.trim().toLowerCase(),
        name: name || null,
        mode,
      }),
    });
  }

  /**
   * Stream an interview response (SSE async generator).
   *
   * Reuses the same SSE parsing pattern as OnboardingService.streamInterviewResponse.
   */
  async *streamResponse(
    token: string,
    sessionId: string,
    content: string,
    skipTopic = false
  ): AsyncGenerator<PublicStreamEvent> {
    const response = await fetch(
      `${BASE_URL}${this.prefix}/${token}/sessions/${sessionId}/respond-stream`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        body: JSON.stringify({ content, skip_topic: skipTopic }),
      }
    );

    if (!response.ok) {
      throw new Error(`Interview stream failed with ${response.status}`);
    }

    if (!response.body) {
      throw new Error('Response body is empty');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { value, done } = await reader.read();
      buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
      buffer = buffer.replace(/\r\n/g, '\n');

      let boundary: number;
      while ((boundary = buffer.indexOf('\n\n')) !== -1) {
        const rawEvent = buffer.slice(0, boundary).trim();
        buffer = buffer.slice(boundary + 2);

        if (!rawEvent) continue;

        const lines = rawEvent.split('\n');
        let eventType = 'message';
        const dataLines: string[] = [];

        for (const line of lines) {
          if (line.startsWith('event:')) {
            eventType = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            dataLines.push(line.slice(5));
          }
        }

        const data = dataLines.join('\n');

        if (eventType === 'done') {
          return;
        }

        try {
          const parsedData = data ? JSON.parse(data) : {};
          yield {
            event_type: eventType as PublicStreamEvent['event_type'],
            data: parsedData,
          };
        } catch (e) {
          console.error('Failed to parse SSE data:', e);
        }
      }

      if (done) break;
    }
  }

  /**
   * Navigate to a specific topic by index.
   */
  async navigateToTopic(
    token: string,
    sessionId: string,
    topicIndex: number
  ): Promise<PublicNavigateTopicResponse> {
    return publicFetch<PublicNavigateTopicResponse>(
      `${this.prefix}/${token}/sessions/${sessionId}/navigate`,
      {
        method: 'POST',
        body: JSON.stringify({ topic_index: topicIndex }),
      }
    );
  }

  /**
   * Complete the interview and get the summary.
   */
  async completeInterview(token: string, sessionId: string): Promise<PublicCompleteResponse> {
    return publicFetch<PublicCompleteResponse>(
      `${this.prefix}/${token}/sessions/${sessionId}/complete`,
      { method: 'POST' }
    );
  }

  /**
   * Get interview summary (after completion).
   */
  async getSummary(token: string, sessionId: string): Promise<PublicSummaryResponse> {
    return publicFetch<PublicSummaryResponse>(
      `${this.prefix}/${token}/sessions/${sessionId}/summary`
    );
  }

  /**
   * Generate review summaries for all topics.
   */
  async generateReview(token: string, sessionId: string): Promise<GenerateReviewResponse> {
    return publicFetch<GenerateReviewResponse>(
      `${this.prefix}/${token}/sessions/${sessionId}/review`,
      { method: 'POST' }
    );
  }

  /**
   * Update a topic's summary with the user's correction.
   */
  async updateReviewTopic(
    token: string,
    sessionId: string,
    topicId: string,
    editedSummary: string
  ): Promise<UpdateReviewResponse> {
    return publicFetch<UpdateReviewResponse>(
      `${this.prefix}/${token}/sessions/${sessionId}/review`,
      {
        method: 'PUT',
        body: JSON.stringify({ topic_id: topicId, edited_summary: editedSummary }),
      }
    );
  }

  /**
   * Confirm all review summaries and complete the interview.
   */
  async confirmReview(token: string, sessionId: string): Promise<ConfirmReviewResponse> {
    return publicFetch<ConfirmReviewResponse>(
      `${this.prefix}/${token}/sessions/${sessionId}/review/confirm`,
      { method: 'POST' }
    );
  }

  /**
   * Return from review to the chat phase.
   */
  async backToChat(token: string, sessionId: string): Promise<{ session_id: string; phase: string }> {
    return publicFetch<{ session_id: string; phase: string }>(
      `${this.prefix}/${token}/sessions/${sessionId}/review/back-to-chat`,
      { method: 'POST' }
    );
  }

  /**
   * Save an inline correction to sidebar knowledge.
   */
  async saveCorrection(
    token: string,
    sessionId: string,
    fieldPath: string,
    correctedValue: any,
    topicId?: string | null,
  ): Promise<SaveCorrectionResponse> {
    return publicFetch<SaveCorrectionResponse>(
      `${this.prefix}/${token}/sessions/${sessionId}/corrections`,
      {
        method: 'POST',
        body: JSON.stringify({
          field_path: fieldPath,
          corrected_value: correctedValue,
          topic_id: topicId ?? null,
        }),
      }
    );
  }
}

const publicInterviewService = new PublicInterviewService();
export default publicInterviewService;
